/**
 * This slice is mostly dedicated to CRM API data
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CallStatus, CurrencyEnum } from '../../enums';
import { GenericStatusByCall } from '../../enums/status-by-call';
import lang from '../../language';
import { CALLS_TO_MEMOIZE } from '../../libSettings';
import {
  AccountDetails,
  ChangeIndividualUserNameRequest,
  IndividualUserNameResponse,
  LegalDeclaration,
  LegalDeclarationsDynamicFilePayload,
  LegalDeclarationsDynamicFileResponse,
  OwnerTradingAccount,
  QuestionaireSuccessPayload,
  RefreshSubscriptionResponse,
  TradingPlan,
  TradingPlatform,
} from '../../models/crm/types';
import {
  ENROLL_METHOD,
  ENROLL_METHOD_NAME,
  ENROLL_REQUEST_STATE,
  ENROLL_REQUEST_STATE_NAME,
  EnrollCRMState,
  FinancialQuestionnaireSection,
  IndividualExtendedInfoData,
  IndividualOwnLegalDeclarationsData,
} from '../../models/enroll';
import { EnrollCall, IdentityDocumentSubTypesEnum } from '../../models/enroll-requests';
import { LoggerService } from '../../services/logger/Logger.service';
import { extractDataNameFromGetCall, formatCurrency, selectNextAccount } from '../../util/DataHelpers';
import { isCallStatusReady } from '../../util/error-handling/StatusByCallHelpers';
import { NullableNumber, NullableString } from '../../util/types';
import {
  accountsFailed,
  enrollSuccess,
  EnrollSuccessCombinedPayload,
  EnrollSuccessPayload,
  ErrorPayload,
  ErrorPayloadFull,
  getAuthUserTradingAcount,
  getCountryListSuccess,
  getIndividualExtendedInfo,
  individualExtendedInfoFailed,
  individualExtendedInfoSuccess,
  logout,
} from '../common-actions';
import { processErrorInReducer } from '../helpers';
import { isUserVerifiedStatus } from '../selectors';

import {
  parseOnboardingErrorCode,
  parseQuestionnaireDataToSections,
  setCRMCallStatus,
  setEnrollStatusByCall,
} from './helpers';
import { CRMCall, LegalDeclarations } from './types';


export interface CRMState {
  connected: boolean;
  individualId: number | null; // cached here for convenience, also present in individualExtendedInfo
  individualUserName: NullableString;
  individualExtendedInfo: IndividualExtendedInfoData | null;
  hasLoadedExtendedInfo: boolean;
  hasLoadedAccounts: boolean;
  selectedAccount: number;
  isUseBonusAccount: boolean;
  bonusAccounts: string[];
  accounts: string[];
  accountDetails: AccountDetails;
  /**
   * tradingPlatformId value cases:
   * (1) has a value - call succeeded.
   * (2) is `undefined` - then call is pending.
   * (3) is `null` - then call failed (timeout or error).
   */
  tradingPlatformId: NullableNumber
  totalAccountValue: string;
  accountInfo: any;
  fundPortalAvailableCash: number;
  fundPortalAccountId: number;
  /**
   * Publicly accessible (no authenticated session needed)
   */
  enroll: EnrollCRMState;
  enrollQueued: (EnrollCall)[];
  error: string | null;
  errorData: any;
  legalDeclarations: LegalDeclaration[];
  statusByCall: GenericStatusByCall<CRMCall>;
  files: { [fileName: string]: string };
  tradingPlans: TradingPlan[];
  planPayment: {
    gateway_url: NullableString;
  }
}

export const INITIAL_STATE: CRMState = {
  connected: false,
  individualExtendedInfo: null,
  hasLoadedExtendedInfo: false,
  hasLoadedAccounts: false,
  selectedAccount: 0,
  individualId: null,
  individualUserName: null,
  isUseBonusAccount: false,
  bonusAccounts: [],
  accounts: [],
  accountDetails: {} as AccountDetails,
  tradingPlatformId: null,
  totalAccountValue: '0',
  accountInfo: {},
  fundPortalAvailableCash: 0,
  fundPortalAccountId: 0,
  enroll: {
    callsInQueue: 0,
    financialQuestionnaireCurrency: CurrencyEnum.EUR,
    financialQuestionnaireSections: [] as FinancialQuestionnaireSection[],
    isEditSourceOfFunds: false,
    enrollProgressInfo: {
      isOpenLastQuestions: false,
      isNoExperienceWithStock: false,
    },
    statusByCall: {},
    bankAccountDetailsOnCreate: {
      account_info: {
        bank_name: '',
        bank_country_id: '',
        bic: '',
        iban: '',
        currency_code: '',
      },
    },
    isPoASent: false,
    isPoISent: false,
    initiallySelectedPlanId: null,
  } as EnrollCRMState,
  enrollQueued: [],
  error: null,
  errorData: null,
  legalDeclarations: [],
  statusByCall: {},
  files: {},
  tradingPlans: [],
  planPayment: {
    gateway_url: null,
  },
};

const logger = LoggerService.getLogger();

const crmSlice = createSlice({
  name: 'crm',
  initialState: INITIAL_STATE,
  reducers: {
    setUseBonusAccount(state, action: PayloadAction<boolean>) {
      state.isUseBonusAccount = action.payload;
    },
    setInitiallySelectedPlanId(state, action) {
      state.enroll.initiallySelectedPlanId = action.payload;
    },
    accountsCompleted(state, action) {
      const accounts = action.payload;
      state.accounts = accounts;

      // if the user has more than 1 account the second one should be a bonus account
      if (accounts.length > 1) {
        const bonusAccount = accounts[1];
        state.bonusAccounts = bonusAccount ? [ bonusAccount ] : [];// 0-> main account 1-> bonus account
      }

      if (state.selectedAccount > state.accounts.length - 1) state.selectedAccount = 0;
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.READY, state);
      setCRMCallStatus(CRMCall.getAccountInfo, CallStatus.PENDING, state);
    },
    accountInfoCompleted(state, action) {
      state.accountInfo = action.payload;
      setCRMCallStatus(CRMCall.getAccountInfo, CallStatus.READY, state);
    },
    accountInfoFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      state.accountInfo = {};
      processErrorInReducer(state, action);
      setCRMCallStatus(CRMCall.getAccountInfo, CallStatus.ERROR, state, action.payload.status);
      setCRMCallStatus(CRMCall.getAccountDetails, CallStatus.PENDING, state);
    },
    accountDetailsCompleted(state, action) {
      state.accountDetails = action.payload;
      state.hasLoadedAccounts = true;
      const { positions_value, unrealized_pn_l, available_cash } = action.payload;
      const totalValue = positions_value + unrealized_pn_l + available_cash;
      state.totalAccountValue = formatCurrency(totalValue, false, true);
    },
    accountDetailsFailed(state, action) {
      state.accountDetails = {} as AccountDetails;
      state.hasLoadedAccounts = false;
      processErrorInReducer(state, action);
      state.totalAccountValue = formatCurrency(0, false, true);
    },
    getNextAccount(state, payload: PayloadAction<string | undefined>) {
      state.hasLoadedAccounts = false;
      const { selected } = selectNextAccount(
        state.isUseBonusAccount ? state.bonusAccounts : state.accounts,
        state.selectedAccount,
      );
      state.selectedAccount = selected;
      state.accountInfo = {};
      state.accountDetails = {} as AccountDetails;
    },
    getTradingPlatforms(state) {
      state.tradingPlatformId = undefined;
      setCRMCallStatus(CRMCall.getTradingPlatforms, CallStatus.PENDING, state);
    },
    getTradingPlatformsSuccess(state, action: PayloadAction<TradingPlatform[]>) {
      const items = action.payload;
      const firstPlatform = items.find(({ trading_platform_name }) => trading_platform_name === '10n');
      state.tradingPlatformId = firstPlatform?.trading_platform_id;
      setCRMCallStatus(CRMCall.getTradingPlatforms, CallStatus.READY, state);
    },
    getTradingPlatformsFailed(state, action) {
      const { status } = action.payload;
      state.tradingPlatformId = null;
      setCRMCallStatus(CRMCall.getTradingPlatforms, CallStatus.ERROR, state, status);
      logger.error(`Getting Trading Platforms failed:\n${JSON.stringify(action.payload)}`);
    },
    refreshSubscription(state) {
      setCRMCallStatus(CRMCall.refreshSubscription, CallStatus.PENDING, state);
    },
    refreshSubscriptionSuccess(state, action: PayloadAction<RefreshSubscriptionResponse>) {
      setCRMCallStatus(CRMCall.refreshSubscription, CallStatus.READY, state);
    },
    refreshSubscriptionFailed(state, action) {
      const { status } = action.payload;
      setCRMCallStatus(CRMCall.refreshSubscription, CallStatus.ERROR, state, status);
      logger.error(`Request "refresh-subscription" failed! payload:\n${JSON.stringify(action.payload)}`);
    },
    getCountryList(state) {
      if (!CALLS_TO_MEMOIZE.getCountryList || !isCallStatusReady(CRMCall.getCountryList, state.statusByCall)) {
        setCRMCallStatus(CRMCall.getCountryList, CallStatus.PENDING, state);
      }
    },
    getCountryListFailed(state, action: PayloadAction<ErrorPayloadFull>) {
      setCRMCallStatus(CRMCall.getCountryList, CallStatus.ERROR, state, action.payload.status);
    },
    enroll(state, action: PayloadAction<EnrollCall>) {
      const { callMethod, callMethodName, callNo, isQueued } = action.payload;

      if (isQueued) state.enrollQueued.shift();
      else state.enroll.callsInQueue++;

      if (state.enroll.callsInQueue <= 1 || isQueued) {
        const requestState = ENROLL_REQUEST_STATE.PENDING;
        state.enroll.status = state.enroll.status === ENROLL_REQUEST_STATE.ERROR && isQueued
          ? state.enroll.status
          : requestState;
        state.enroll.statusByCall[callNo ? `${callMethod}_${callNo}` : callMethod] = {
          callMethodName: callMethodName as ENROLL_METHOD_NAME,
          requestState,
          requestStateName: ENROLL_REQUEST_STATE[requestState] as ENROLL_REQUEST_STATE_NAME,
        };
        state.enroll.callMethod = callMethod;
        state.enroll.callMethodName = callMethodName;
        state.error = null;
        state.errorData = {};
      }
    },
    enrollFail(state, action) {
      const {
        callName: callNameOther,
        callMethodName: callMethodNameCRM,
        callMethod: callMethodCRM,
        customData,
        errorStr,
        error,
        errorData,
      } = action.payload;

      const callMethodName = callMethodNameCRM ?? callNameOther;
      const callMethod = callMethodCRM ?? ENROLL_METHOD[callMethodName];
      const { callNo } = customData ?? {};

      if (state.enroll.callsInQueue > 0) {
        state.enroll.callsInQueue--;
      } else {
        console.warn('[crm/index] Trying to decrease callsInQueue while it is 0');
      }

      const requestState = parseOnboardingErrorCode(errorData.status);
      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.statusByCall[callNo ? `${callMethod as ENROLL_METHOD}_${callNo}` : callMethod as ENROLL_METHOD] = {
        callMethodName,
        requestState,
        requestStateName: ENROLL_REQUEST_STATE[requestState] as ENROLL_REQUEST_STATE_NAME,
        errorCode: errorData.status,
      };
      state.enroll.callMethod = callMethod;
      state.enroll.callMethodName = callMethodName;
      state.error = error;
      state.errorData = action.payload;
      logger.error(errorStr);
    },
    enrollPersonalDetailsWithTaxDetails(state, action) {
      const { callMethod, isPersonalDetailsUpdate } = action.payload;

      state.enroll.callsInQueue++;
      state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
      state.enroll.callMethod = callMethod;
      state.enroll.callMethodName = ENROLL_METHOD[callMethod];
      state.error = null;
      state.errorData = {};

      const requestState = ENROLL_REQUEST_STATE.PENDING;
      const method = (
        isPersonalDetailsUpdate ? ENROLL_METHOD.putEnrollmentOwnPersonal : ENROLL_METHOD.postEnrollmentOwnPersonal
      );

      state.enroll.statusByCall[method] = {
        callMethodName: ENROLL_METHOD[method] as ENROLL_METHOD_NAME,
        requestState,
        requestStateName: ENROLL_REQUEST_STATE[requestState] as ENROLL_REQUEST_STATE_NAME,
      };
    },
    clearEnrollPoiState(state) {
      state.enroll.isPoISent = false;
      state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
    },
    clearEnrollPoaState(state) {
      state.enroll.isPoASent = false;
      state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
    },
    clearCrmEnrollStatusByCode(state) {
      state.enroll.statusByCall = {};
    },
    enrollPersonalDetailsSuccess(state, action) {
      const { isPersonalDetailsUpdate } = action.payload.customData || { };
      if (state.enroll.callsInQueue > 0) {
        state.enroll.callsInQueue--;
      }

      state.error = null;
      state.errorData = {};

      const { individualData } = action.payload.customData;

      if (individualData && state.individualExtendedInfo) {
        state.individualExtendedInfo = {
          ...state.individualExtendedInfo,
          ...individualData,
        };
      }

      if (state.enroll.callsInQueue === 0) {
        state.enroll.status = ENROLL_REQUEST_STATE.READY;
      }

      const requestState = ENROLL_REQUEST_STATE.READY;
      const method = (
        isPersonalDetailsUpdate ? ENROLL_METHOD.putEnrollmentOwnPersonal : ENROLL_METHOD.postEnrollmentOwnPersonal
      );

      state.enroll.statusByCall[method].requestState = requestState;
      state.enroll.statusByCall[method].requestStateName = (
        ENROLL_REQUEST_STATE[requestState] as ENROLL_REQUEST_STATE_NAME
      );
    },
    enrollPersonalDetailsFailed(state, action) {
      const {
        error,
        errorStr,
        callMethod,
        customData,
      } = action.payload;

      if (state.enroll.callsInQueue > 0) {
        state.enroll.callsInQueue--;
      }

      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.callMethod = callMethod;
      state.enroll.callMethodName = ENROLL_METHOD[callMethod];
      state.error = error;
      state.errorData = action.payload;

      const requestState = ENROLL_REQUEST_STATE.READY;
      const method = (
        customData?.isPersonalDetailsUpdate
          ? ENROLL_METHOD.putEnrollmentOwnPersonal
          : ENROLL_METHOD.postEnrollmentOwnPersonal
      );

      state.enroll.statusByCall[method].requestState = requestState;
      state.enroll.statusByCall[method].requestStateName = (
        ENROLL_REQUEST_STATE[requestState] as ENROLL_REQUEST_STATE_NAME
      );

      logger.error(errorStr);
    },
    enrollTaxDetails(state, action) {},
    enrollAddressesBatch(state, action) {
      const { callMethod, callMethodName } = action.payload;

      state.enroll.callsInQueue++;
      state.enroll.status = ENROLL_REQUEST_STATE.PENDING;
      state.enroll.callMethod = callMethod;
      state.enroll.callMethodName = callMethodName;
      state.error = null;
      state.errorData = {};
    },
    enrollAddressesBatchSuccess(state, action) {
      if (state.enroll.callsInQueue > 0) {
        state.enroll.callsInQueue--;
      }

      state.error = null;
      state.errorData = {};

      if (state.enroll.callsInQueue === 0) {
        state.enroll.status = ENROLL_REQUEST_STATE.READY;
      }
    },
    enrollAddressesBatchFail(state, action) {
      state.enroll.callsInQueue--;
      const {
        error,
        errorStr,
        callMethod,
      } = action.payload;

      state.enroll.status = ENROLL_REQUEST_STATE.ERROR;
      state.enroll.callMethod = callMethod;
      state.enroll.callMethodName = ENROLL_METHOD[callMethod];
      state.error = error;
      state.errorData = action.payload;
      logger.error(errorStr);
    },
    enrollAddCallToQueue(state, action: PayloadAction<EnrollCall>) {
      state.enrollQueued.push(action.payload);
    },
    enrolSaveState(state, action: PayloadAction<string>) {
      if (
        action.payload === IdentityDocumentSubTypesEnum.identityCard
        || action.payload === IdentityDocumentSubTypesEnum.passport
      ) {
        state.enroll.isPoISent = true;
      } else if (action.payload === 'poa') {
        state.enroll.isPoASent = true;
      }
    },
    enrollSaveCustom(state, action: PayloadAction<any>) {
      state.enroll.custom = action.payload;
    },
    getEnrollFinancialQuestionnaire(state) {
      state.error = null;
      state.errorData = {};

      const method = ENROLL_METHOD.postFinancialQuestionnaire;
      const requestState = ENROLL_REQUEST_STATE.PENDING;

      setEnrollStatusByCall(state, requestState, method);
    },
    getEnrollFinancialQuestionTypesSuccess(state: CRMState, action) {},
    getEnrollFinancialQuestionnaireSuccess(state: CRMState, action: PayloadAction<QuestionaireSuccessPayload>) {
      const data = action.payload.customData;
      const { types, answers } = data;

      state.error = null;
      state.errorData = {};

      if (types && answers) {
        state.enroll.financialQuestionnaireSections = parseQuestionnaireDataToSections(data);
      }

      const method = ENROLL_METHOD.postFinancialQuestionnaire;
      const requestState = ENROLL_REQUEST_STATE.READY;

      setEnrollStatusByCall(state, requestState, method);
    },
    getEnrollFinancialQuestionnaireFail(state: CRMState, action) {
      state.error = action.payload.error;
      state.errorData = action.payload;
      const { error } = action.payload;
      logger.error(error);
    },
    setFinancialQuestionnaireCurrency(state, action: PayloadAction<CurrencyEnum>) {
      state.enroll.financialQuestionnaireCurrency = action.payload;
    },
    setSourceOfFundsIsEdit(state, action) {
      state.enroll.isEditSourceOfFunds = action.payload;
    },
    setEnrollProgressInfo(state, action) {
      state.enroll.enrollProgressInfo = action.payload;
    },
    getLegalDeclarations(state, action: PayloadAction<{ active: boolean, isVerified: boolean }>) {
      setCRMCallStatus(CRMCall.getLegalDeclarations, CallStatus.PENDING, state);
    },
    getLegalDeclarationsCompleted(state, action: PayloadAction<LegalDeclaration[]>) {
      const { customData: { isVerified } } = action.payload as any;

      const legalDeclarations = action.payload.reduce((acc: LegalDeclaration[], current: LegalDeclaration) => {
        if (acc.length > 0 && current.name !== LegalDeclarations.PrivacyPolicy) {
          let itemName = current.name;

          switch (current.name) {
            case LegalDeclarations.ClientAgreement: itemName = lang.commonClientAgreement(); break;
            case LegalDeclarations.GeneralTerms: itemName = lang.commonGeneralTermsAndConditions(); break;
            default: break;
          }

          const tempCurrent = current;
          tempCurrent.name = itemName;
          return [ ...acc, tempCurrent ];
        }

        if (current.name === LegalDeclarations.PrivacyPolicy) {
          return [ ...acc ];
        }

        return [ ...acc, current ];
      }, []);

      state.legalDeclarations = legalDeclarations;
      setCRMCallStatus(CRMCall.getLegalDeclarations, CallStatus.READY, state);
    },
    getLegalDeclarationsFailed(state, action: PayloadAction<ErrorPayload>) {
      const error = action.payload?.error || action.payload;
      logger.error(error);
      setCRMCallStatus(CRMCall.getLegalDeclarations, CallStatus.ERROR, state);
    },
    setBankAccountDetailsOnCreate(state, action) {
      state.enroll.bankAccountDetailsOnCreate.account_info = action.payload;
    },
    setIntialEnrollStatusByCall(state, action) {
      const requestState = ENROLL_REQUEST_STATE.INITIAL;

      if (state.enroll.statusByCall[action.payload]) {
        Object.assign(state.enroll.statusByCall[action.payload], {
          requestState: ENROLL_REQUEST_STATE.INITIAL,
          requestStateName: ENROLL_REQUEST_STATE[requestState],
          errorCode: null,
        });
      }
    },
    getLegalDeclarationsDynamicFile(state: CRMState, action: PayloadAction<LegalDeclarationsDynamicFilePayload>) {
      const method = ENROLL_METHOD.getLegalDeclarationsDynamicFile;
      const requestState = ENROLL_REQUEST_STATE.PENDING;
      setEnrollStatusByCall(state, requestState, method);
    },
    // eslint-disable-next-line max-len
    getLegalDeclarationsDynamicFileSuccess(state: CRMState, action: PayloadAction<LegalDeclarationsDynamicFileResponse>) {
      const { customData: { fileName }, data } = action.payload;
      const method = ENROLL_METHOD.getLegalDeclarationsDynamicFile;
      const requestState = ENROLL_REQUEST_STATE.READY;

      setEnrollStatusByCall(state, requestState, method);
      state.files[fileName] = data;
    },
    getLegalDeclarationsDynamicFileFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error, errorData } = action.payload;
      const method = ENROLL_METHOD.getLegalDeclarationsDynamicFile;
      const requestState = ENROLL_REQUEST_STATE.ERROR;

      setEnrollStatusByCall(state, requestState, method, errorData.status);
      state.error = error;
    },
    updateIndividualTransitionToForVerification(state: CRMState) {
      setCRMCallStatus(CRMCall.updateIndividualTransitionToForVerification, CallStatus.PENDING, state);
    },
    updateIndividualTransitionToForVerificationSuccess(
      state: CRMState,
    ) {
      setCRMCallStatus(CRMCall.updateIndividualTransitionToForVerification, CallStatus.READY, state);
    },
    updateIndividualTransitionToForVerificationFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error, errorData } = action.payload;
      setCRMCallStatus(CRMCall.updateIndividualTransitionToForVerification, CallStatus.ERROR, state);
      state.error = error;
    },
    updateIndividualTransitionToForActive(state: CRMState) {
      setCRMCallStatus(CRMCall.updateIndividualTransitionToForActive, CallStatus.PENDING, state);
    },
    updateIndividualTransitionToForActiveSuccess(
      state: CRMState,
    ) {
      setCRMCallStatus(CRMCall.updateIndividualTransitionToForActive, CallStatus.READY, state);
    },
    updateIndividualTransitionToForActiveFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.updateIndividualTransitionToForActive, CallStatus.ERROR, state);
      state.error = error;
    },
    getIndividualUserName(state: CRMState) {
      setCRMCallStatus(CRMCall.getIndividualUserName, CallStatus.PENDING, state);
    },
    getIndividualUserNameSuccess(state: CRMState, action: PayloadAction<IndividualUserNameResponse>) {
      const { profile_name } = action.payload;
      state.individualUserName = profile_name;
      setCRMCallStatus(CRMCall.getIndividualUserName, CallStatus.READY, state);
    },
    getIndividualUserNameFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.getIndividualUserName, CallStatus.ERROR, state);
      state.error = error;
    },
    changeIndividualUserName(state: CRMState, action: PayloadAction<ChangeIndividualUserNameRequest>) {
      setCRMCallStatus(CRMCall.changeIndividualUserName, CallStatus.PENDING, state);
    },
    changeIndividualUserNameSuccess(state: CRMState, action: PayloadAction<IndividualUserNameResponse>) {
      const { profile_name } = action.payload;
      state.individualUserName = profile_name;
      if (!isUserVerifiedStatus(state.individualExtendedInfo)) {
        state.individualExtendedInfo = null;// after the set username it will re-fetch extended info
      }
      setCRMCallStatus(CRMCall.changeIndividualUserName, CallStatus.READY, state);
    },
    changeIndividualUserNameFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error, status } = action.payload;
      setCRMCallStatus(CRMCall.changeIndividualUserName, CallStatus.ERROR, state, status);
      state.error = error;
    },
    getAccountsSummary(state: CRMState) {
      setCRMCallStatus(CRMCall.getAccountsSummary, CallStatus.PENDING, state);
    },
    getAccountsSummarySuccess(state: CRMState, action) {
      const account = action.payload.filter(
        (acc: OwnerTradingAccount) => acc.is_cash_account === true,
      );
      state.fundPortalAvailableCash = account[0] && account[0].available_cash ? account[0].available_cash : 0;
      state.fundPortalAccountId = account[0] && account[0].account_id ? account[0].account_id : 0;
      setCRMCallStatus(CRMCall.getAccountsSummary, CallStatus.READY, state);
    },
    getAccountsSummaryFailed(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setCRMCallStatus(CRMCall.getAccountsSummary, CallStatus.ERROR, state, status);
    },
    internalTransfer(state: CRMState, action) {
      setCRMCallStatus(CRMCall.internalTransfer, CallStatus.PENDING, state);
    },
    internalTransferSuccess(state: CRMState, action) {
      setCRMCallStatus(CRMCall.internalTransfer, CallStatus.READY, state);
    },
    internalTransferFailed(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { status } = action.payload;
      setCRMCallStatus(CRMCall.internalTransfer, CallStatus.ERROR, state, status);
    },

    searchTradingPlans(state: CRMState, action) {
      setCRMCallStatus(CRMCall.searchTradingPlans, CallStatus.PENDING, state);
    },
    searchTradingPlansSuccess(state: CRMState, action: PayloadAction<any>) {
      const payload = action.payload;
      state.tradingPlans = [ payload[0] as TradingPlan ];
      setCRMCallStatus(CRMCall.searchTradingPlans, CallStatus.READY, state);
    },
    searchTradingPlansFail(state: CRMState, action: PayloadAction<ErrorPayloadFull>) {
      const { error } = action.payload;
      setCRMCallStatus(CRMCall.searchTradingPlans, CallStatus.ERROR, state);
      state.error = error;
    },
  },
  extraReducers: {
    [getCountryListSuccess.type]: (state, action) => {
      setCRMCallStatus(CRMCall.getCountryList, CallStatus.READY, state);
    },
    [getIndividualExtendedInfo.type](state) {
      state.hasLoadedExtendedInfo = false;
      setCRMCallStatus(CRMCall.getIndividualExtendedInfo, CallStatus.PENDING, state);
    },
    [individualExtendedInfoSuccess.type]: (state, action) => {
      state.individualId = action.payload?.id;
      state.individualExtendedInfo = action.payload;
      state.connected = true;
      state.hasLoadedExtendedInfo = true;
      setCRMCallStatus(CRMCall.getIndividualExtendedInfo, CallStatus.READY, state);
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.PENDING, state);
    },
    [getAuthUserTradingAcount.type]: state => {
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.PENDING, state);
    },
    [individualExtendedInfoFailed.type]: (state, action) => {
      state.connected = false;
      state.hasLoadedExtendedInfo = true;
      setCRMCallStatus(
        CRMCall.getIndividualExtendedInfo,
        CallStatus.ERROR,
        state,
        JSON.stringify(action.payload) as any,
      );
    },
    [accountsFailed.type]: (state, action) => {
      state.enroll.callsInQueue--;
      state.accounts = [];
      processErrorInReducer(state, action);
      state.hasLoadedAccounts = false;
      setCRMCallStatus(CRMCall.getAccounts, CallStatus.ERROR, state, (action.payload as ErrorPayloadFull).status);
    },
    [enrollSuccess.type]: (state, action: { payload: EnrollSuccessCombinedPayload }) => {
      if (state.enroll.callsInQueue > 0) {
        state.enroll.callsInQueue--;
      } else {
        console.warn('[crm/index] Trying to decrease callsInQueue while it is 0');
      }

      state.error = null;
      state.errorData = {};

      const { callName, status, customData } = (action.payload as EnrollSuccessPayload);
      const { callNo } = customData ?? {};

      if (!callName) {
        console.warn(`[crm/index] Callname not defined for '${action}' => ${callName}`, action);
        return;
      }

      const isGet = callName.indexOf('get') === 0;
      const isPost = callName.indexOf('post') === 0;
      const isPut = callName.indexOf('put') === 0;
      const isDelete = callName.indexOf('delete') === 0;

      const requestState = ENROLL_REQUEST_STATE.READY;

      if (isGet) {
        const dataName: string = extractDataNameFromGetCall(callName);
        if (callName === ENROLL_METHOD[ENROLL_METHOD.getIndividualExtendedInfo]) {
          state.individualExtendedInfo = action.payload as IndividualExtendedInfoData;
        } else if (ENROLL_METHOD[callName] === ENROLL_METHOD.getBusinessOwn) {
          const dataPayload: any = action.payload;

          // ART-2713 Removing first item - "Not Specified"
          (state.enroll as any)[dataName] = dataPayload.slice(1);
        } else {
          (state.enroll as any)[dataName] = action.payload;
        }
      } else if (isPost) {
        // nothing so far
      } else if (isPut) {
        if (callName === ENROLL_METHOD[ENROLL_METHOD.putIndividualOwnLegalDeclarations]) {
          // This case is the only currently known PUT that is used also for populating data in Redux state
          // Its data is also populated by the `isGet` `if` above - using generic approach with these two code fragments:
          //  1) const dataName: string = extractDataNameFromGetCall(callName);
          //  2) (state.enroll as any)[dataName] = action.payload;
          state.enroll.individualOwnLegalDeclarations = action.payload as any as IndividualOwnLegalDeclarationsData;
        }
      } else if (isDelete) {
        // nothing so far
      } else {
        console.warn(`[crm/index] Unknown enroll success case - ${callName}`);
      }
      if (state.enroll.callsInQueue === 0) state.enroll.status = requestState;

      const callMethod = ENROLL_METHOD[callName];
      state.enroll.statusByCall[callNo ? `${callMethod}_${callNo}` : callMethod] = {
        callMethodName: callName,
        requestState,
        requestStateName: ENROLL_REQUEST_STATE[requestState] as ENROLL_REQUEST_STATE_NAME,
      };
    },
    [logout.type]: state => ({
      ...INITIAL_STATE,
    }),
  },
});

export const {
  setInitiallySelectedPlanId,
  setUseBonusAccount,
  accountsCompleted,
  accountInfoCompleted,
  accountInfoFailed,
  accountDetailsCompleted,
  accountDetailsFailed,
  getNextAccount,
  getTradingPlatforms,
  getTradingPlatformsSuccess,
  getTradingPlatformsFailed,
  refreshSubscription,
  refreshSubscriptionSuccess,
  refreshSubscriptionFailed,
  getCountryList,
  getCountryListFailed,
  enroll,
  enrollFail,
  enrollTaxDetails,
  enrollPersonalDetailsWithTaxDetails,
  enrollPersonalDetailsFailed,
  enrollPersonalDetailsSuccess,
  clearEnrollPoiState,
  clearEnrollPoaState,
  clearCrmEnrollStatusByCode,
  enrollAddressesBatch,
  enrollAddressesBatchFail,
  enrollAddressesBatchSuccess,
  enrollAddCallToQueue,
  enrolSaveState,
  enrollSaveCustom,
  getEnrollFinancialQuestionnaire,
  getEnrollFinancialQuestionnaireFail,
  getEnrollFinancialQuestionnaireSuccess,
  setFinancialQuestionnaireCurrency,
  setSourceOfFundsIsEdit,
  setEnrollProgressInfo,
  getLegalDeclarations,
  getLegalDeclarationsCompleted,
  getLegalDeclarationsFailed,
  setBankAccountDetailsOnCreate,
  setIntialEnrollStatusByCall,
  getLegalDeclarationsDynamicFile,
  getLegalDeclarationsDynamicFileSuccess,
  getLegalDeclarationsDynamicFileFail,
  updateIndividualTransitionToForVerification,
  updateIndividualTransitionToForVerificationSuccess,
  updateIndividualTransitionToForVerificationFail,
  updateIndividualTransitionToForActive,
  updateIndividualTransitionToForActiveSuccess,
  updateIndividualTransitionToForActiveFail,
  getIndividualUserName,
  getIndividualUserNameSuccess,
  getIndividualUserNameFail,
  changeIndividualUserName,
  changeIndividualUserNameSuccess,
  changeIndividualUserNameFail,
  getAccountsSummary,
  getAccountsSummarySuccess,
  getAccountsSummaryFailed,
  internalTransfer,
  internalTransferSuccess,
  internalTransferFailed,
  searchTradingPlans,
  searchTradingPlansSuccess,
  searchTradingPlansFail,
} = crmSlice.actions;

export default crmSlice.reducer;
